import React, { useEffect, useMemo, useState } from "react";
import { Col, Container, Row } from "react-bootstrap";
import { Alert, Button, Empty, Input, Spin, message } from "antd";
import { PlusOutlined, SearchOutlined } from "@ant-design/icons";
import CmsTopBar from "components/cmslayout/CmsTopBar";
import type { RapFormValues } from "./types";
import RapFormModal from "./components/RapFormModal";
import DeleteRapModal from "./components/DeleteRapModal";
import RapCard from "./components/RapCard";
import martEmptyImg from "assets/images/mart-empty.png";
import { useAppDispatch, useAppSelector } from "store/hooks";
import {
  clearRepsError,
  createRep,
  deleteRep,
  fetchReps,
  updateRep,
} from "store/slices/repsSlice";
import { clearMarketError, fetchMarkets } from "store/slices/marketSlice";
import type { RepPayload } from "types/rep";

interface AddRapPageProps {}

export const AddRapPage: React.FunctionComponent<AddRapPageProps> = () => {
  const dispatch = useAppDispatch();
  const {
    actionLoading,
    error: repsError,
    loading: repsLoading,
    reps,
  } = useAppSelector((state) => state.reps);
  const {
    error: marketsError,
    loading: marketsLoading,
    markets,
  } = useAppSelector((state) => state.markets);
  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [search, setSearch] = useState("");

  useEffect(() => {
    dispatch(fetchReps());
  }, [dispatch]);

  const editingRap = useMemo(
    () => reps.find((rep) => rep.id === editingId) ?? null,
    [reps, editingId]
  );

  const deletingRap = useMemo(
    () => reps.find((rep) => rep.id === deletingId) ?? null,
    [reps, deletingId]
  );

  const filteredRaps = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return reps;

    return reps.filter((rep) =>
      [
        rep.name,
        rep.email,
        rep.phone,
        rep.market_name || "",
        rep.market_location || "",
      ].some((value) => value.toLowerCase().includes(q))
    );
  }, [reps, search]);

  const loadMarketsForModal = () => {
    if (markets.length === 0 && !marketsLoading) {
      dispatch(fetchMarkets());
    }
  };

  const openAdd = () => {
    dispatch(clearRepsError());
    dispatch(clearMarketError());
    setEditingId(null);
    setFormOpen(true);
    loadMarketsForModal();
  };

  const openEdit = (id: number) => {
    dispatch(clearRepsError());
    dispatch(clearMarketError());
    setEditingId(id);
    setFormOpen(true);
    loadMarketsForModal();
  };

  const closeForm = () => {
    if (actionLoading) return;
    setFormOpen(false);
    setEditingId(null);
  };

  const requestDelete = (id: number) => {
    dispatch(clearRepsError());
    setDeletingId(id);
    setDeleteOpen(true);
  };

  const closeDelete = () => {
    if (actionLoading) return;
    setDeleteOpen(false);
    setDeletingId(null);
  };

  const confirmDelete = async () => {
    if (!deletingId) return;
    const name = reps.find((rep) => rep.id === deletingId)?.name;
    const result = await dispatch(deleteRep(deletingId));

    if (deleteRep.fulfilled.match(result)) {
      closeDelete();
      message.success(`Rap${name ? ` (${name})` : ""} deleted.`);
      return;
    }

    message.error(result.payload || "Unable to delete RAP.");
  };

  const buildPayload = (values: RapFormValues): RepPayload => {
    const password = values.password?.trim();
    return {
      name: values.name.trim(),
      email: values.email.trim(),
      phone: values.phone.trim(),
      ...(password ? { password } : {}),
      market_id: Number(values.market_id),
    };
  };

  const handleSubmit = async (values: RapFormValues) => {
    const payload = buildPayload(values);

    if (editingId && editingRap) {
      const result = await dispatch(updateRep({ id: editingId, payload }));

      if (!updateRep.fulfilled.match(result)) {
        message.error(result.payload || "Unable to update RAP.");
        return;
      }

      setFormOpen(false);
      setEditingId(null);
      message.success("Rap updated.");
      return;
    }

    const createPayload = {
      ...payload,
      password: values.password?.trim() || "",
    };
    const result = await dispatch(createRep(createPayload));

    if (!createRep.fulfilled.match(result)) {
      message.error(result.payload || "Unable to create RAP.");
      return;
    }

    setFormOpen(false);
    message.success("Rap added.");
  };

  const error = repsError || marketsError;

  return (
    <React.Fragment>
      <CmsTopBar title="Rap" />

      <Container fluid className="dashboard-wrap">
        <Container fluid className="px-3 py-4">
          <div className="markets-section-header mb-3">
            <h2 className="markets-heading">Raps ({filteredRaps.length})</h2>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              className="markets-add-btn"
              onClick={openAdd}
            >
              Add New Rap
            </Button>
          </div>

          <Row className="mb-3">
            <Col span={24}>
              <Input
                placeholder="Search"
                prefix={<SearchOutlined className="markets-search-icon" />}
                className="markets-search markets-search-full"
                allowClear
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
            </Col>
          </Row>

          {error && (
            <Alert
              type="error"
              message={error}
              showIcon
              closable
              className="mb-3"
              onClose={() => {
                dispatch(clearRepsError());
                dispatch(clearMarketError());
              }}
            />
          )}

          <div className="markets-empty-wrap">
            {repsLoading ? (
              <Spin size="large" />
            ) : filteredRaps.length === 0 ? (
              <Empty
                image={
                  <div
                    style={{
                      height: 130,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <img
                      src={martEmptyImg}
                      alt="No RAPs"
                      style={{
                        width: 120,
                        height: 120,
                        objectFit: "contain",
                        display: "block",
                      }}
                    />
                  </div>
                }
                description={
                  <div style={{ textAlign: "center", marginTop: 12 }}>
                    <div
                      style={{
                        fontSize: 20,
                        fontWeight: 600,
                        color: "#1f1f1f",
                      }}
                    >
                      No RAPs found.
                    </div>
                  </div>
                }
              />
            ) : (
              <div className="markets-card-grid">
                {filteredRaps.map((rap) => (
                  <RapCard
                    key={rap.id}
                    rap={rap}
                    onEdit={openEdit}
                    onDeleteRequest={requestDelete}
                  />
                ))}
              </div>
            )}
          </div>
        </Container>
      </Container>

      <RapFormModal
        open={formOpen}
        mode={editingId ? "edit" : "add"}
        markets={markets}
        marketsLoading={marketsLoading}
        loading={actionLoading}
        initialRap={editingRap}
        onClose={closeForm}
        onSubmit={handleSubmit}
      />

      <DeleteRapModal
        open={deleteOpen}
        rapName={deletingRap?.name}
        loading={actionLoading}
        onClose={closeDelete}
        onConfirm={confirmDelete}
      />
    </React.Fragment>
  );
};

export default AddRapPage;
